import { useTimeSettings } from '@mnd-frontend/contexts';
import { monthNameLong, monthNameShort } from '@mnd-frontend/dates';
import { BrandReportGroupStatisticsBy, BrandReportTimespan } from '@mnd-frontend/graphql-schema';
import { useCallback } from 'react';
import { TimeSpan, timeSpanString } from '../components/common/TimeSpan';
import { useReportStaticData, useReportTranslation } from '../contexts';
import type { TranslationFunction } from '../types';

const getSentimentText = ({
  t,
  sentimentType,
}: {
  t: TranslationFunction;
  sentimentType: 'positive' | 'negative' | 'neutral';
}) => {
  switch (sentimentType) {
    case 'positive':
      return t('reports_sentiment-positive', { count: 1 });
    case 'negative':
      return t('reports_sentiment-negative', { count: 1 });
    case 'neutral':
      return t('reports_sentiment-neutral', { count: 1 });
  }
};

export const useReportGraphHelpers = () => {
  const { t, lng } = useReportTranslation();
  const {
    material: { timezone },
  } = useTimeSettings();
  const { timespan } = useReportStaticData();

  const getLegendCaption = useCallback(
    ({ fromDate, toDate }: { fromDate: Date; toDate: Date }) => {
      switch (timespan) {
        case BrandReportTimespan.LastHalfYear:
          return t('reports_last_half_year_period', {
            period: fromDate.getMonth() > 5 ? 2 : 1,
            year: fromDate.getFullYear(),
          });
        case BrandReportTimespan.LastQuarter:
          return t('reports_last_quarter_period', {
            period: Math.ceil((fromDate.getMonth() + 1) / 3),
            year: fromDate.getFullYear(),
          });
        case BrandReportTimespan.LastMonth:
          return (
            <time
              dateTime={
                fromDate.getFullYear() + '-' + (fromDate.getMonth() + 1).toString().padStart(2, '0')
              }
            >
              {monthNameLong(fromDate, lng)}
            </time>
          );
        case BrandReportTimespan.LastYear:
          return (
            <time dateTime={fromDate.getFullYear().toString()}>
              {fromDate.getFullYear().toString()}
            </time>
          );
        default:
          return <TimeSpan fromDate={fromDate} toDate={toDate} timezone={timezone} locale={lng} />;
      }
    },
    [lng, t, timespan, timezone],
  );
  const getLegendCaptionString = useCallback(
    ({ fromDate, toDate }: { fromDate: Date; toDate: Date }) => {
      switch (timespan) {
        case BrandReportTimespan.LastHalfYear:
          return t('reports_last_half_year_period', {
            period: fromDate.getMonth() > 5 ? 2 : 1,
            year: fromDate.getFullYear(),
          });
        case BrandReportTimespan.LastQuarter:
          return t('reports_last_quarter_period', {
            period: Math.ceil((fromDate.getMonth() + 1) / 3),
            year: fromDate.getFullYear(),
          });
        case BrandReportTimespan.LastMonth:
          return monthNameLong(fromDate, lng);
        case BrandReportTimespan.LastYear:
          return fromDate.getFullYear().toString();
        default:
          return timeSpanString({ fromDate, toDate, timezone, locale: lng });
      }
    },
    [lng, t, timespan, timezone],
  );

  const getSentimentLegendCaption = useCallback(
    ({
      fromDate,
      toDate,
      sentimentType,
    }: {
      fromDate: Date;
      toDate: Date;
      sentimentType: 'positive' | 'negative' | 'neutral';
    }) => {
      return (
        <>
          {getSentimentText({ t, sentimentType })} {getLegendCaption({ fromDate, toDate })}
        </>
      );
    },
    [getLegendCaption, t],
  );

  const getPeriodLabel = useCallback(
    (
      groupBy: BrandReportGroupStatisticsBy,
      year: number,
      month: number | null,
      week: number | null,
    ) => {
      if (groupBy === BrandReportGroupStatisticsBy.Quarter && month) {
        return monthNameShort(new Date(year, month - 1, 1), lng);
      } else {
        return t('common_week-number-short', { week_number: week ?? '' });
      }
    },
    [lng, t],
  );

  const getPeriodLabelLong = useCallback(
    (
      groupBy: BrandReportGroupStatisticsBy,
      year: number,
      month: number | null,
      week: number | null,
    ) => {
      if (groupBy === BrandReportGroupStatisticsBy.Quarter && month) {
        return monthNameLong(new Date(year, month - 1, 1), lng);
      } else {
        return t('common_week-number-short', { week_number: week ?? '' }) + ' ' + year;
      }
    },
    [lng, t],
  );

  return {
    getLegendCaption,
    getSentimentLegendCaption,
    getPeriodLabel,
    getPeriodLabelLong,
    getLegendCaptionString,
  };
};
